import { useMemo, useState } from "react";
import { Link } from "react-router";
import { ModeSelect } from "../components/Dialogs.tsx";
import { Confirm, CopyButton, Empty, ErrorBox, Field, Loading, Modal, StatePill, Toggle, useAction, useNow } from "../components/ui.tsx";
import { BUG_OUTCOME_LABELS, dailyBranch, DEFAULT_BUG_BRANCH_PATTERN, DEFAULT_BUG_COLUMNS, isTimeZone, parseSheetUrl } from "@switchboard/shared";
import { del, errorText, patch, post, put, refresh, useApi } from "../lib/api.ts";
import { ago, fullTime, plural } from "../lib/format.ts";
import type { BugPollSummary, BugSheet, BugSheetColumns, BugsOverview, BugTask, Project } from "../lib/types.ts";

/** Bug sheets: Google Sheets polled for open bugs, fixed in one session per codebase per day. */
export function Bugs() {
  const bugs = useApi<BugsOverview>("/api/bugs", { refreshInterval: 15_000 });
  const projects = useApi<Project[]>("/api/projects");
  const [adding, setAdding] = useState(false);
  if (!bugs.data) return bugs.error ? <ErrorBox error={bugs.error} retry={() => bugs.mutate()} /> : <Loading />;
  const { sheets, google } = bugs.data;

  return (
    <>
      <div className="pagehead">
        <div>
          <h1>Bug sheets</h1>
          <div className="sub">
            Open bugs on a mapped branch are fixed in one session per codebase per day, on <span className="mono">bug-fix/DD-MM-YY</span>, then
            pushed, put in a PR and written to the report sheet.
          </div>
        </div>
        <button className="btn primary" onClick={() => setAdding(true)}>
          Add sheet
        </button>
      </div>
      <GoogleAccess google={google} />
      {sheets.length === 0 ? (
        <div className="card">
          <Empty title="No bug sheets yet">
            <button className="btn primary" onClick={() => setAdding(true)}>
              Add a bug sheet
            </button>
          </Empty>
        </div>
      ) : (
        sheets.map((s) => <SheetCard key={s.id} sheet={s} projects={projects.data ?? []} />)
      )}
      {sheets.length > 0 && <BugTasks />}
      {adding && <SheetDialog onClose={() => setAdding(false)} />}
    </>
  );
}

function GoogleAccess({ google }: { google: BugsOverview["google"] }) {
  const [editing, setEditing] = useState(false);
  const [key, setKey] = useState("");
  const [save, saving] = useAction(async (value: string | null) => {
    await put("/api/bugs/google", { serviceAccount: value });
    setEditing(false);
    setKey("");
    refresh("/api/bugs");
  }, "Saved");

  return (
    <div className="card stack">
      <div className="row between">
        <h3>Google access</h3>
        {google.configured && !editing && (
          <div className="row">
            <button className="btn sm" onClick={() => setEditing(true)}>
              Replace key
            </button>
            <button className="btn sm danger" disabled={saving} onClick={() => save(null)}>
              Remove
            </button>
          </div>
        )}
      </div>
      {google.configured ? (
        <div className="row small">
          Service account <span className="mono">{google.email ?? "(unreadable key)"}</span>
          {google.email && <CopyButton text={google.email} label="Copy email" />}
          <span className="muted">Share each report sheet with this address as an Editor (private bug sheets as a Viewer).</span>
        </div>
      ) : (
        <div className="callout warn small">
          Reading works for sheets shared as "anyone with the link can view". Writing the reports needs a Google service account: in Google Cloud,
          enable the Google Sheets API, create a service account, add a JSON key, and paste the key here. Then share each report sheet with the
          account's email as an Editor.
        </div>
      )}
      {(!google.configured || editing) && (
        <form
          className="stack tight"
          onSubmit={(e) => {
            e.preventDefault();
            void save(key);
          }}
        >
          <Field label="Service account key (JSON)" hint="Stored encrypted; only its email is shown again.">
            <textarea className="mono" rows={4} value={key} onChange={(e) => setKey(e.target.value)} placeholder='{"type": "service_account", "client_email": "…", "private_key": "…"}' />
          </Field>
          <div className="row">
            <button className="btn primary" disabled={saving || !key.trim()}>
              Save key
            </button>
            {editing && (
              <button type="button" className="btn" onClick={() => setEditing(false)}>
                Cancel
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}

function summaryLine(s: BugPollSummary): string {
  return `${plural(s.open, "open bug")} of ${s.rows}${s.picked ? ` · ${s.picked} picked up` : ""}`;
}

function SheetCard({ sheet, projects }: { sheet: BugSheet; projects: Project[] }) {
  const now = useNow(30_000);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [mapBranch, setMapBranch] = useState<string | null>(null);
  const [poll, polling] = useAction(
    async () => {
      const s = await post<BugPollSummary>(`/api/bugs/sheets/${sheet.id}/poll`);
      refresh("/api/bugs");
      return s;
    },
    (s) => (s.picked ? `Picked up ${plural(s.picked, "bug")}` : "No new bugs to pick up"),
  );
  const [setEnabled, toggling] = useAction(async (enabled: boolean) => {
    await patch(`/api/bugs/sheets/${sheet.id}`, { enabled });
    refresh("/api/bugs");
  });
  const unmapped = Object.entries(sheet.lastSummary?.unmapped ?? {});

  return (
    <div className="card stack">
      <div className="row between">
        <div className="stack tight">
          <div className="row">
            <h3>{sheet.name}</h3>
            {sheet.enabled ? <span className="pill ok">every {plural(sheet.pollMinutes, "min", "min")}</span> : <span className="pill">paused</span>}
          </div>
          <div className="row small">
            <a href={sheet.sheetUrl} target="_blank" rel="noreferrer">
              Bug sheet ↗
            </a>
            <a href={sheet.reportUrl} target="_blank" rel="noreferrer">
              Report sheet ↗
            </a>
            <span className="muted" title={sheet.lastPolledAt ? fullTime(sheet.lastPolledAt) : undefined}>
              {sheet.lastPolledAt ? `polled ${ago(sheet.lastPolledAt, now)}` : "not polled yet"}
              {sheet.lastSummary && ` · ${summaryLine(sheet.lastSummary)}`}
            </span>
          </div>
        </div>
        <div className="row">
          <button className="btn sm" disabled={polling} onClick={() => poll()}>
            {polling ? "Polling…" : "Poll now"}
          </button>
          <button className="btn sm" disabled={toggling} onClick={() => setEnabled(!sheet.enabled)}>
            {sheet.enabled ? "Pause" : "Resume"}
          </button>
          <button className="btn sm" onClick={() => setEditing(true)}>
            Edit
          </button>
          <button className="btn sm danger" onClick={() => setDeleting(true)}>
            Delete
          </button>
        </div>
      </div>
      {sheet.lastError && <div className="callout bad small">{sheet.lastError}</div>}
      {unmapped.length > 0 && (
        <div className="callout warn small row">
          <span>Open bugs on branches with no codebase:</span>
          {unmapped.map(([branch, n]) => (
            <button key={branch} className="btn sm" onClick={() => setMapBranch(branch)}>
              Map <span className="mono">{branch}</span> ({n})
            </button>
          ))}
        </div>
      )}
      {sheet.lastSummary && sheet.lastSummary.noBranch > 0 && (
        <div className="muted small">
          {plural(sheet.lastSummary.noBranch, "open bug")} {sheet.lastSummary.noBranch === 1 ? "has" : "have"} no branch selected and {sheet.lastSummary.noBranch === 1 ? "is" : "are"} left alone.
        </div>
      )}
      <Routes sheet={sheet} projects={projects} mapBranch={mapBranch} onMapped={() => setMapBranch(null)} />
      {editing && <SheetDialog sheet={sheet} onClose={() => setEditing(false)} />}
      {deleting && (
        <Confirm
          title={`Delete ${sheet.name}?`}
          confirmLabel="Delete sheet"
          danger
          onClose={() => setDeleting(false)}
          onConfirm={async () => {
            await del(`/api/bugs/sheets/${sheet.id}`);
            refresh("/api/bugs");
          }}
        >
          <p>
            Stops polling it and forgets its mappings and bug history. Sessions, branches and PRs stay, and nothing in the Google Sheets changes.
          </p>
        </Confirm>
      )}
    </div>
  );
}

function Routes({ sheet, projects, mapBranch, onMapped }: { sheet: BugSheet; projects: Project[]; mapBranch: string | null; onMapped: () => void }) {
  const today = new Date();
  const [adding, setAdding] = useState(false);
  const showForm = adding || mapBranch !== null || sheet.routes.length === 0;
  const [remove, removing] = useAction(async (id: string) => {
    await del(`/api/bugs/routes/${id}`);
    refresh("/api/bugs");
  }, "Mapping removed");

  return (
    <div className="stack tight">
      <h4>Branch → codebase</h4>
      {sheet.routes.length > 0 && (
        <div className="tablewrap">
          <table className="table" style={{ minWidth: 720 }}>
            <thead>
              <tr>
                <th>Branch in the sheet</th>
                <th>Codebase</th>
                <th>Base branch</th>
                <th>Today's branch</th>
                <th>Permission mode</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sheet.routes.map((r) => (
                <tr key={r.id}>
                  <td className="mono">{r.branch}</td>
                  <td>
                    <Link to={`/projects/${r.project.slug}`}>{r.project.name}</Link>
                  </td>
                  <td className="mono">{r.baseBranch ?? r.branch}</td>
                  <td className="mono">{dailyBranch(r.branchPattern, today, sheet.timezone)}</td>
                  <td className="small">{r.permissionMode ?? <span className="muted">project default</span>}</td>
                  <td>
                    <button className="btn sm ghost" disabled={removing} onClick={() => remove(r.id)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showForm ? (
        <AddRoute
          key={mapBranch ?? ""}
          sheet={sheet}
          projects={projects}
          initialBranch={mapBranch ?? ""}
          onAdded={() => {
            setAdding(false);
            onMapped();
          }}
          onCancel={sheet.routes.length ? () => (setAdding(false), onMapped()) : undefined}
        />
      ) : (
        <div>
          <button className="btn sm" onClick={() => setAdding(true)}>
            Map another branch
          </button>
        </div>
      )}
    </div>
  );
}

function AddRoute({
  sheet,
  projects,
  initialBranch,
  onAdded,
  onCancel,
}: {
  sheet: BugSheet;
  projects: Project[];
  initialBranch: string;
  onAdded: () => void;
  onCancel?: () => void;
}) {
  const [branch, setBranch] = useState(initialBranch);
  const [project, setProject] = useState("");
  const [base, setBase] = useState("");
  const [pattern, setPattern] = useState(DEFAULT_BUG_BRANCH_PATTERN);
  const [mode, setMode] = useState("bypassPermissions");
  const active = projects.filter((p) => p.status === "active");
  const target = active.find((p) => p.slug === project);
  const [add, adding] = useAction(async () => {
    await post(`/api/bugs/sheets/${sheet.id}/routes`, {
      branch: branch.trim(),
      project,
      baseBranch: base.trim() || undefined,
      branchPattern: pattern.trim(),
      permissionMode: mode || undefined,
    });
    setBranch("");
    setBase("");
    refresh("/api/bugs");
    onAdded();
  }, "Mapped. Poll now to pick up its open bugs.");
  const preview = useMemo(() => {
    try {
      return dailyBranch(pattern, new Date(), sheet.timezone);
    } catch {
      return "";
    }
  }, [pattern, sheet.timezone]);

  return (
    <form
      className="stack tight"
      onSubmit={(e) => {
        e.preventDefault();
        void add();
      }}
    >
      <div className="grid3">
        <Field label="Branch in the sheet" hint="As written in the Branch column; case doesn't matter.">
          <input className="mono" value={branch} onChange={(e) => setBranch(e.target.value)} placeholder="production" autoFocus={Boolean(initialBranch)} />
        </Field>
        <Field label="Codebase">
          <select value={project} onChange={(e) => setProject(e.target.value)}>
            <option value="" disabled>
              Pick a project
            </option>
            {active.map((p) => (
              <option key={p.id} value={p.slug}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Base branch" hint="Pulled from origin; the daily branch is cut from it and the PR goes into it.">
          <input className="mono" value={base} onChange={(e) => setBase(e.target.value)} placeholder={branch.trim() || "same as the sheet's branch"} />
        </Field>
        <Field label="Daily branch" hint={preview ? <>Today: <span className="mono">{preview}</span></> : "{DD}, {MM}, {YY}, {YYYY}"}>
          <input className="mono" value={pattern} onChange={(e) => setPattern(e.target.value)} />
        </Field>
        <Field label="Permission mode" hint="Unattended fixing needs a mode that doesn't stop to ask for approval.">
          <ModeSelect value={mode} onChange={setMode} allowInherit={target?.permissionMode ?? "acceptEdits"} />
        </Field>
      </div>
      <div className="row">
        <button className="btn primary" disabled={adding || !branch.trim() || !project}>
          {adding ? "Checking origin…" : "Add mapping"}
        </button>
        {onCancel && (
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

const browserZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

const COLUMN_LABELS: Record<keyof BugSheetColumns, string> = {
  bug: "Bug number",
  status: "Status",
  branch: "Branch",
  title: "Title",
  reportBug: "Report: bug number",
  reportText: "Report: report",
  reportPr: "Report: PR link",
};

function SheetDialog({ sheet, onClose }: { sheet?: BugSheet; onClose: () => void }) {
  const [form, setForm] = useState({
    name: sheet?.name ?? "",
    sheetUrl: sheet?.sheetUrl ?? "",
    reportUrl: sheet?.reportUrl ?? "",
    pollMinutes: sheet?.pollMinutes ?? 10,
    timezone: sheet?.timezone ?? browserZone(),
    openStatus: sheet?.openStatus ?? "Open",
    enabled: sheet?.enabled ?? true,
    columns: sheet?.columns ?? DEFAULT_BUG_COLUMNS,
  });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const urlError = (u: string) => (u.trim() && !parseSheetUrl(u) ? "a Google Sheets link" : undefined);
  const zoneOk = isTimeZone(form.timezone.trim());
  const ok = form.name.trim() && parseSheetUrl(form.sheetUrl) && parseSheetUrl(form.reportUrl) && zoneOk && form.pollMinutes >= 1;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = { ...form, name: form.name.trim(), timezone: form.timezone.trim() };
      if (sheet) await patch(`/api/bugs/sheets/${sheet.id}`, body);
      else await post("/api/bugs/sheets", body);
      refresh("/api/bugs");
      onClose();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={sheet ? `Edit ${sheet.name}` : "Add a bug sheet"}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={busy || !ok} onClick={submit}>
            {busy ? "Reading the sheet…" : sheet ? "Save" : "Add sheet"}
          </button>
        </>
      }
    >
      <Field label="Name">
        <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Agency OS bugs" autoFocus />
      </Field>
      <Field label="Bug sheet" hint="Link to the tab with the bugs (the gid in the link picks the tab)." error={urlError(form.sheetUrl)}>
        <input className="mono" value={form.sheetUrl} onChange={(e) => set("sheetUrl", e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/…/edit#gid=…" />
      </Field>
      <Field label="Report sheet" hint="Each finished bug gets a row: bug number, report and PR link." error={urlError(form.reportUrl)}>
        <input className="mono" value={form.reportUrl} onChange={(e) => set("reportUrl", e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/…" />
      </Field>
      <div className="grid3">
        <Field label="Poll every (minutes)">
          <input type="number" min={1} max={1440} value={form.pollMinutes} onChange={(e) => set("pollMinutes", Number(e.target.value))} />
        </Field>
        <Field label="Time zone" hint="For the date in the daily branch." error={zoneOk ? undefined : "an IANA zone, e.g. Asia/Kolkata"}>
          <input className="mono" value={form.timezone} onChange={(e) => set("timezone", e.target.value)} />
        </Field>
        <Field label="Pick up bugs with status">
          <input value={form.openStatus} onChange={(e) => set("openStatus", e.target.value)} />
        </Field>
      </div>
      <Toggle checked={form.enabled} onChange={(v) => set("enabled", v)} label="Poll this sheet" />
      <details>
        <summary className="small">Column names</summary>
        <div className="grid3">
          {(Object.keys(COLUMN_LABELS) as (keyof BugSheetColumns)[]).map((k) => (
            <Field key={k} label={COLUMN_LABELS[k]}>
              <input value={form.columns[k]} onChange={(e) => set("columns", { ...form.columns, [k]: e.target.value })} />
            </Field>
          ))}
        </div>
      </details>
      {error && <div className="callout bad">{error}</div>}
    </Modal>
  );
}

const TASK_TONE: Record<BugTask["status"], string> = { queued: "", working: "accent", reported: "accent", done: "ok", failed: "bad" };
const TASK_LABEL: Record<BugTask["status"], string> = { queued: "Queued", working: "Fixing", reported: "Publishing", done: "Done", failed: "Failed" };

function BugTasks() {
  const tasks = useApi<BugTask[]>("/api/bugs/tasks?limit=200", { refreshInterval: 10_000 });
  const now = useNow(30_000);
  const [retry, retrying] = useAction(async (id: string) => {
    await post(`/api/bugs/tasks/${id}/retry`);
    refresh("/api/bugs");
  }, "Sent again");

  if (!tasks.data) return tasks.error ? <ErrorBox error={tasks.error} /> : <Loading />;
  return (
    <div className="card flush">
      <div className="row between" style={{ padding: "12px 16px 0" }}>
        <h3>Bugs</h3>
        <span className="muted small">{plural(tasks.data.length, "bug")} picked up</span>
      </div>
      {tasks.data.length === 0 ? (
        <Empty title="No bugs picked up yet">Map a branch above, then poll the sheet.</Empty>
      ) : (
        <div className="tablewrap">
          <table className="table" style={{ minWidth: 960 }}>
            <thead>
              <tr>
                <th>Bug</th>
                <th>Status</th>
                <th>Branch · session</th>
                <th>PR</th>
                <th>Report</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tasks.data.map((t) => {
                const notFixed = t.status === "done" && t.outcome && t.outcome !== "fixed";
                return (
                  <tr key={t.id}>
                    <td>
                      <b>{t.bugKey}</b> {t.title}
                      <div className="muted small">
                        {t.sheetName} · <span className="mono">{t.sheetBranch}</span> · {ago(t.createdAt, now)}
                      </div>
                    </td>
                    <td>
                      <span className={`pill ${notFixed ? "warn" : TASK_TONE[t.status]}`}>
                        {t.status === "done" && t.outcome ? BUG_OUTCOME_LABELS[t.outcome] : TASK_LABEL[t.status]}
                      </span>
                    </td>
                    <td className="small">
                      {t.run ? (
                        <>
                          <span className="mono">{t.run.branch}</span>
                          {t.run.sessionId && t.session && (
                            <div className="row nowrap">
                              <Link to={`/sessions/${t.run.sessionId}`}>{t.session.name}</Link>
                              <StatePill state={t.session.state} />
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="small">
                      {(t.prUrl ?? "")
                        .split("\n")
                        .filter(Boolean)
                        .map((u) => (
                          <div key={u}>
                            <a href={u} target="_blank" rel="noreferrer">
                              {u.replace(/^https:\/\/github\.com\//, "")} ↗
                            </a>
                          </div>
                        ))}
                    </td>
                    <td className="small" style={{ maxWidth: 420 }}>
                      {t.error && (
                        <div className="callout bad small">
                          {t.error}
                          {t.status === "reported" && <div className="muted">Retried every few minutes; fix the cause and it goes through.</div>}
                        </div>
                      )}
                      {t.summary && (
                        <details>
                          <summary>{t.summary.length > 80 ? `${t.summary.slice(0, 80)}…` : t.summary}</summary>
                          <p style={{ whiteSpace: "pre-wrap" }}>{t.summary}</p>
                          {t.testing && <p style={{ whiteSpace: "pre-wrap" }}>Testing: {t.testing}</p>}
                        </details>
                      )}
                    </td>
                    <td>
                      {(t.status === "done" || t.status === "failed") && (
                        <button className="btn sm" disabled={retrying} onClick={() => retry(t.id)} title="Send this bug to today's session again">
                          Retry
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
